import * as z from "zod";
import { billingPeriodSchema } from "./get-bills";

const getBillMonthSummariesInput_ = z.object({
  // The month to total, as its start instant (UTC midnight on the 1st) taken
  // verbatim from a returned `months[].monthStart`. Omitted → newest month.
  month: z.date().optional(),
});
export const getBillMonthSummariesInput: z.ZodType<GetBillMonthSummariesInput> =
  getBillMonthSummariesInput_;
export interface GetBillMonthSummariesInput extends z.infer<
  typeof getBillMonthSummariesInput_
> {}

const monthSummarySchema_ = z.object({
  // UTC month-start instant (midnight on the 1st). Doubles as the stable key
  // the client echoes back as `input.month` to total the month.
  monthStart: z.date(),
  // Inclusive UTC month-end instant (last ms of the month), so the client can
  // scope the invoices table's billed-date range with no timezone math.
  monthEnd: z.date(),
  // Distinct billing cadences across the month's bills (e.g. a bimonthly CRO
  // bill alongside the monthly software cycle). The card joins them.
  cadences: z.array(billingPeriodSchema),
  invoiceCount: z.number(),
  // The month carries the still-open returns bill, whose total is live and
  // still accruing — the card badges it as in progress.
  hasOpenBill: z.boolean(),
});
export const monthSummarySchema: z.ZodType<MonthSummary> = monthSummarySchema_;
export interface MonthSummary extends z.infer<typeof monthSummarySchema_> {}

const selectedMonthSchema_ = z.object({
  monthStart: z.date(),
  total: z.number(),
  currency: z.string().default("USD"),
});
export const selectedMonthSchema: z.ZodType<SelectedMonth> =
  selectedMonthSchema_;
export interface SelectedMonth extends z.infer<typeof selectedMonthSchema_> {}

const getBillMonthSummariesOutput_ = z.object({
  // Newest month first. Skeleton only (no money) — a cheap `bill`-table scan.
  months: z.array(monthSummarySchema),
  // The one totaled month (`input.month`, else newest). Null when the account
  // has no billed months. Only this month's charges are summed, so the read
  // costs the same as totaling a single current period.
  selected: selectedMonthSchema_.nullable(),
});
export const getBillMonthSummariesOutput: z.ZodType<GetBillMonthSummariesOutput> =
  getBillMonthSummariesOutput_;
export interface GetBillMonthSummariesOutput extends z.infer<
  typeof getBillMonthSummariesOutput_
> {}

export const getBillMonthSummaries = {
  name: "getBillMonthSummaries" as const,
  input: getBillMonthSummariesInput,
  output: getBillMonthSummariesOutput,
};
